import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../../utils/api';

const emptyForm = {
  name: '',
  sort_order: 0,
  turnaround_minutes: 60,
  description: '',
  is_active: true,
};

export default function AdminHelpdeskSeverities() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    api.get('/helpdesk/admin/severities').then((r) => setRows(r.data?.data || []));
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name,
      sort_order: row.sort_order,
      turnaround_minutes: row.turnaround_minutes,
      description: row.description || '',
      is_active: row.is_active,
    });
    setOpen(true);
  };

  const save = async () => {
    if (editing) {
      await api.put(`/helpdesk/admin/severities/${editing.id}`, form);
    } else {
      await api.post('/helpdesk/admin/severities', form);
    }
    setOpen(false);
    load();
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove severity "${row.name}"?`)) return;
    await api.delete(`/helpdesk/admin/severities/${row.id}`);
    load();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Help desk severities
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define severity levels and expected turnaround (in minutes). These appear when users create tickets.
      </Typography>
      <Button startIcon={<AddIcon />} variant="contained" onClick={openNew} sx={{ mb: 2 }}>
        Add severity
      </Button>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Turnaround (minutes)</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.sort_order}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell>{r.turnaround_minutes}</TableCell>
                <TableCell>{r.is_active ? 'Yes' : 'No'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(r)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => remove(r)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit severity' : 'New severity'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label="Sort order"
            type="number"
            fullWidth
            margin="normal"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
          />
          <TextField
            label="Turnaround (minutes)"
            type="number"
            fullWidth
            margin="normal"
            helperText="e.g. 60 = 1 hour, 1440 = 1 day"
            value={form.turnaround_minutes}
            onChange={(e) =>
              setForm({ ...form, turnaround_minutes: parseInt(e.target.value, 10) || 1 })
            }
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            minRows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!form.name?.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
